import { useState } from 'react';
import './CookieSettings.css';
import CookiePolicyPanel from './CookiePolicyPanel';

const TOGGLE_ON = 'var(--color-primary)';
const TOGGLE_OFF = 'rgba(255,255,255,0.15)';

function Toggle({ on, onChange }) {
  return (
    <div
      onClick={onChange}
      style={{
        width: '48px',
        height: '26px',
        borderRadius: '13px',
        backgroundColor: on ? TOGGLE_ON : TOGGLE_OFF,
        position: 'relative',
        cursor: 'pointer',
        flexShrink: 0,
        transition: 'background-color 0.2s',
      }}
    >
      <div style={{
        position: 'absolute',
        top: '3px',
        left: on ? '25px' : '3px',
        width: '20px',
        height: '20px',
        borderRadius: '50%',
        backgroundColor: '#fff',
        transition: 'left 0.2s',
        boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
      }} />
    </div>
  );
}

function CookieCategory({ icon, title, badge, badgeColor, description, toggle, onToggle }) {
  const [expanded, setExpanded] = useState(icon === '-');

  return (
    <div style={{ borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '18px', marginTop: '18px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <button
          onClick={() => setExpanded(e => !e)}
          className="cs-cat-toggle-btn"
          style={{
            background: 'none',
            border: 'none',
            color: '#fff',
            cursor: 'pointer',
            width: '22px',
            lineHeight: 1,
            padding: 0,
            flexShrink: 0,
          }}
        >
          {expanded ? '−' : '+'}
        </button>
        <span className="cs-cat-title" style={{
          flex: 1,
          color: '#fff',
          fontFamily: 'var(--font-primary)',
          fontWeight: 700,
        }}>
          {title}
        </span>
        {badge ? (
          <span className="cs-cat-badge" style={{
            color: badgeColor || TOGGLE_ON,
            fontFamily: 'var(--font-primary)',
            fontWeight: 700,
          }}>
            {badge}
          </span>
        ) : (
          <Toggle on={toggle} onChange={onToggle} />
        )}
      </div>
      {expanded && description && (
        <p className="cs-cat-desc" style={{
          color: 'rgba(255,255,255,0.65)',
          fontFamily: 'var(--font-primary)',
          lineHeight: 1.65,
          margin: '12px 0 0 34px',
        }}>
          {description}
        </p>
      )}
    </div>
  );
}

function CookieSettings({ onClose, onAcceptAll, onConfirm }) {
  const [analytics, setAnalytics] = useState(true);
  const [preference, setPreference] = useState(false);
  const [showPolicy, setShowPolicy] = useState(false);

  const handleConfirm = () => {
    onConfirm({ analytics, preference });
  };

  return (
    <>
      <CookiePolicyPanel open={showPolicy} onClose={() => setShowPolicy(false)} hideBackdrop />

      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          zIndex: 10000,
        }}
      />

      {/* Panel */}
      <div style={{
        position: 'fixed',
        top: 0,
        right: 0,
        bottom: 0,
        zIndex: 10001,
        width: 'min(480px, 100vw)',
        backgroundColor: '#1a1a1f',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '-8px 0 40px rgba(0,0,0,0.6)',
      }}>
        {/* Scrollable body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '32px 28px 24px' }}>
          {/* Header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
            <h2 className="cs-heading" style={{
              color: '#fff',
              fontFamily: 'var(--font-primary)',
              fontWeight: 700,
              margin: 0,
            }}>
              Cookie settings
            </h2>
            <button
              onClick={onClose}
              className="cs-close-btn"
              style={{
                background: 'none',
                border: 'none',
                color: '#fff',
                cursor: 'pointer',
                lineHeight: 1,
                padding: '2px 4px',
              }}
            >
              ✕
            </button>
          </div>

          {/* Intro text */}
          <p className="cs-body" style={{
            color: 'rgba(255,255,255,0.7)',
            fontFamily: 'var(--font-primary)',
            lineHeight: 1.7,
            margin: '0 0 6px',
          }}>
            This panel allows you to customize your privacy preferences on Payible.
            We use cookies to maintain infrastructure security, verify your account
            identity, and keep your transaction pipeline safe.
          </p>
          <p className="cs-body" style={{
            color: 'rgba(255,255,255,0.7)',
            fontFamily: 'var(--font-primary)',
            lineHeight: 1.7,
            margin: '0',
          }}>
            You can freely toggle your performance and analytical tracking preferences
            below. Adjusting these settings helps our team optimize live asset chart
            speeds and platform response times. For full details, read our{' '}
            <button
              onClick={() => setShowPolicy(true)}
              style={{
                background: 'none',
                border: 'none',
                color: '#fff',
                fontWeight: 700,
                fontFamily: 'var(--font-primary)',
                fontSize: 'inherit',
                cursor: 'pointer',
                padding: 0,
                textDecoration: 'underline',
              }}
            >
              Full Cookie Policy.
            </button>
          </p>

          {/* Cookie categories */}
          <CookieCategory
            icon="-"
            title="Essential cookies"
            badge="Always Active"
            badgeColor={TOGGLE_ON}
            description="Mandatory for platform security and core functionality. They handle secure session handshakes, verify your authentication states, prevent fraudulent exploits, and maintain active fiat-to-crypto transaction pipelines. Without these, secure platform access is impossible."
          />
          <CookieCategory
            icon="-"
            title="Analytics cookies"
            toggle={analytics}
            onToggle={() => setAnalytics(v => !v)}
            description="Allows our team to collect aggregated, anonymous data regarding platform traffic. This specifically monitors system latency, server response times, and the loading speed of our live asset grids, directly helping us optimize app performance."
          />
          <CookieCategory
            icon="+"
            title="Preference cookies"
            toggle={preference}
            onToggle={() => setPreference(v => !v)}
            description="Stores your display and interface preferences such as language, region, and layout settings so the platform remembers your configuration between sessions."
          />
        </div>

        {/* Footer buttons */}
        <div style={{
          padding: '20px 28px',
          borderTop: '1px solid rgba(255,255,255,0.1)',
          display: 'flex',
          gap: '12px',
        }}>
          <button
            onClick={handleConfirm}
            className="cs-footer-btn"
            style={{
              flex: 1,
              padding: '12px 0',
              backgroundColor: 'transparent',
              color: '#fff',
              border: '1px solid rgba(255,255,255,0.25)',
              borderRadius: '8px',
              fontFamily: 'var(--font-primary)',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Confirm my choices
          </button>
          <button
            onClick={onAcceptAll}
            className="cs-footer-btn"
            style={{
              flex: 1,
              padding: '12px 0',
              backgroundColor: 'transparent',
              color: '#fff',
              border: '1px solid rgba(255,255,255,0.25)',
              borderRadius: '8px',
              fontFamily: 'var(--font-primary)',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Accept All
          </button>
        </div>
      </div>
    </>
  );
}

export default CookieSettings;
